import getLogger from '@ignitesales/logger-es';
import { ignite } from '../ignite.mjs';
import {FlowSteps} from '../components/employee-login/constants/flowSteps.mjs';

const logger = getLogger({ module: 'login-action-creators' });

const LoginActions = {
  BRANCHES_LOADED: 'BRANCHES_LOADED',
  BRANCH_SEARCH_STARTED: 'BRANCH_SEARCH_STARTED',
  BRANCH_SEARCH_FAILED: 'BRANCH_SEARCH_FAILED',
  EMPLOYEES_LOADED: 'EMPLOYEES_LOADED',
  EMPLOYEES_CONFLICTS_SEARCH_STARTED: 'EMPLOYEES_CONFLICTS_SEARCH_STARTED',
  EMPLOYEES_CONFLICTS_LOADED: 'EMPLOYEES_CONFLICTS_LOADED',
  EMPLOYEE_SEARCH_STARTED: 'EMPLOYEE_SEARCH_STARTED',
  EMPLOYEE_SEARCH_FAILED: 'EMPLOYEE_SEARCH_FAILED',
  EMPLOYEE_UPDATE_STARTED: 'EMPLOYEE_UPDATE_STARTED',
  EMPLOYEE_UPDATE_SUCCESSFUL: 'EMPLOYEE_UPDATE_SUCCESSFUL',
  EMPLOYEE_UPDATE_ERROR: 'EMPLOYEE_UPDATE_ERROR',
  EMPLOYEE_UPDATE_FAILED: 'EMPLOYEE_UPDATE_FAILED',
  EMPLOYEE_SELECTED: 'EMPLOYEE_SELECTED',
  EMPLOYEE_LOGGED_IN: 'EMPLOYEE_LOGGED_IN',
  EMPLOYEE_LOGIN_STARTED: 'EMPLOYEE_LOGIN_STARTED',
  EMPLOYEE_LOGIN_FAILED: 'EMPLOYEE_LOGIN_FAILED',
  LOGIN_FLOW_CHANGED: 'LOGIN_FLOW_CHANGED',
  LOGIN_FLOW_ENABLED: 'LOGIN_FLOW_ENABLED',
  LOGGED_OUT: 'LOGGED_OUT',
  LOG_OUT_FAILED: 'LOG_OUT_FAILED'
};

const enableLoginFlow = selectedEmployeeId => {
  return async (dispatch) => {
    if(selectedEmployeeId) {
      const employee = await ignite.employeeLoginService.get(selectedEmployeeId);
      if (employee) {
        dispatch( employeeSelected(employee) );
        dispatch(onLoginFlowChange({ stepToAdd: FlowSteps.PROFILE_SELECTED }));
      }
    }
    dispatch({type: LoginActions.LOGIN_FLOW_ENABLED});
  };
};

const loadEmployees = searchValue => {
  return async (dispatch) => {
    try {
      dispatch( startProcessing(LoginActions.EMPLOYEE_SEARCH_STARTED) );
      const employees = await ignite.employeeLoginService.search(searchValue);
      dispatch( employeesLoaded(employees) );
    } catch (ex) {
      logger.error('Error occurred while loading employees', ex);
      dispatch(loginRequestFailed(LoginActions.EMPLOYEE_SEARCH_FAILED, ex));
    }
  };
};

const loadEmployeeById = ({selectedEmployeeId, thirdPartyId}) => {
  return async (dispatch) => {
    try {
      if (selectedEmployeeId || thirdPartyId) {
        logger.log('Logging in with selected employee', {selectedEmployeeId, thirdPartyId});
        const employee = await ignite.employeeLoginService.get(selectedEmployeeId || thirdPartyId);
        if (employee) {
          dispatch( employeeSelected(employee) );
        } else {
          logger.log('Looking for employee, but found none');
        }
      }
    } catch (ex) {
      logger.error('An error occurred while attempting to login by employee Id', ex);
      dispatch( loginRequestFailed(LoginActions.EMPLOYEE_SEARCH_FAILED, ex) );
    }
  };
};

const loadBranches = searchValue => {
  return async (dispatch) => {
    try {
      dispatch( startProcessing(LoginActions.BRANCH_SEARCH_STARTED) );
      if (searchValue.length >= 3) {
        logger.log('searching for branches', searchValue);
        const branches = await ignite.employeeLoginService.findBranches(searchValue);
        dispatch( branchesLoaded(branches) );
      }else{
        dispatch( branchesLoaded([]) );
      }
    } catch (ex) {
      logger.error('Error occured while searching for branches', ex);
      dispatch(loginRequestFailed( LoginActions.BRANCH_SEARCH_FAILED, ex));
    }
  };
};

const saveEmployeeProfile = employee => {
  return async (dispatch) => {
    try {
      dispatch( startProcessing(LoginActions.EMPLOYEE_UPDATE_STARTED) );
      const result = await ignite.employeeLoginService.save(employee);

      if(result.status === 'success') {
        dispatch({ type: LoginActions.EMPLOYEE_UPDATE_SUCCESSFUL, selectedEmployee: result.employee });
      } else {
        if(result.errors) {
          dispatch({ type: LoginActions.EMPLOYEE_UPDATE_ERROR, errors: result.errors });
        }
        dispatch( loginRequestFailed(LoginActions.EMPLOYEE_UPDATE_FAILED) );
      }
    } catch (ex) {
      logger.error('Error occurred while saving employee profile', ex);
      dispatch(loginRequestFailed(LoginActions.EMPLOYEE_UPDATE_FAILED, ex));
    }
  };
};

const selectEmployee = employee => {
  return async (dispatch) => {
    const selectedEmployee = {
      ...employee
    };
    dispatch( {type: LoginActions.EMPLOYEE_SELECTED, selectedEmployee });
  };
};

const searchEmployeeConflicts = ({email: searchByEmail, employeeId: searchById}) => {
  return async (dispatch, getState) => {
    try {
      dispatch( startProcessing(LoginActions.EMPLOYEES_CONFLICTS_SEARCH_STARTED) );
      const employeeLoginState = getState().employeeLoginState;
      const currentEmployee = employeeLoginState.selectedEmployee || {};
      const result = [];
      let byEmail = null;
      if(searchByEmail) {
        byEmail = await ignite.employeeLoginService.searchByEmail(searchByEmail);
        if (byEmail && byEmail.id && byEmail.id !== currentEmployee.id) {
          byEmail.conflictType = 'email';
          result.push(byEmail);
        }
      }
      if(searchById) {
        const byId = await ignite.employeeLoginService.searchByThirdPartyId(searchById);
        if (byId && byId.id && byId.id !== currentEmployee.id) {
          byId.conflictType = 'id';
          if(!byEmail || byEmail.id !== byId.id) {
            result.push(byId);
          }
        }
      }
      dispatch( {type: LoginActions.EMPLOYEES_CONFLICTS_LOADED, result} );
    } catch (ex) {
      logger.error('Error occurred while searching for conflicts', ex);
      dispatch(loginRequestFailed(LoginActions.EMPLOYEE_SEARCH_FAILED, ex));
    }
  };
};

const loginEmployee = ({employee, branch}) => {
  return async (dispatch) => {
    try {
      dispatch( startProcessing(LoginActions.EMPLOYEE_LOGIN_STARTED) );
      const result = await ignite.employeeLoginService.login({
        employee,
        branch
      });

      if(result.status === 'success') {
        logger.log('Employee logged in', result);
        dispatch( {type: LoginActions.EMPLOYEE_LOGGED_IN, employee, branch} );
      }else{
        dispatch(loginRequestFailed(LoginActions.EMPLOYEE_LOGIN_FAILED));
      }
    } catch (ex) {
      logger.error('An error occurred while logging in employee', ex);
      dispatch(loginRequestFailed(LoginActions.EMPLOYEE_LOGIN_FAILED, ex));
    }
  };
};

const onLoginFlowChange = ({stepToAdd, stepToRemove, shouldReset}) => {
  return async (dispatch) => {
    dispatch({
      type: LoginActions.LOGIN_FLOW_CHANGED,
      stepToAdd,
      stepToRemove,
      shouldReset
    });
  };
};

const logout = () => {
  return async (dispatch) => {
    dispatch({type: LoginActions.LOGGED_OUT});
    fetch('/logout')
      .then(response => {
        if(!response.ok){
          dispatch(loginRequestFailed(LoginActions.LOG_OUT_FAILED, response));
        }
      })
      .catch(ex => {
        logger.error('Failed to logout', ex);
        dispatch(loginRequestFailed(LoginActions.LOG_OUT_FAILED, ex));
      });
  };
};

const startProcessing = (type) => {
  return {
    type
  };
};

const employeesLoaded = employees => {
  return {
    type: LoginActions.EMPLOYEES_LOADED,
    employees
  };
};

const employeeSelected = selectedEmployee => {
  return {
    type: LoginActions.EMPLOYEE_SELECTED,
    selectedEmployee
  };
};

const branchesLoaded = branches => {
  return {
    type: LoginActions.BRANCHES_LOADED,
    branches
  };
};

const loginRequestFailed = (type, error) => {
  return {
    type,
    error
  };
};

export {
  LoginActions,
  enableLoginFlow,
  loadEmployees,
  loadEmployeeById,
  loadBranches,
  saveEmployeeProfile,
  searchEmployeeConflicts,
  selectEmployee,
  loginEmployee,
  onLoginFlowChange,
  logout
};
